import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Pagination from "@mui/material/Pagination";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Controllers
import {
  sendNotification,
  getNotificationHistory,
} from "data/controllers/notificationsController";
import { getPlansByRole } from "data/controllers/usersController";

function Notifications() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination state
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit] = useState(10);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Form state
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [targetType, setTargetType] = useState("all");
  const [targetRole, setTargetRole] = useState("");
  const [targetPlan, setTargetPlan] = useState("");
  const [sendSuccess, setSendSuccess] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);

  const tabs = [
    { label: "Send Notification", value: 0 },
    { label: "History", value: 1 },
  ];

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleRoleChange = (event) => {
    setTargetRole(event.target.value);
    // Reset target plan when role changes (plans will be filtered)
    setTargetPlan("");
  };

  const handleTargetTypeChange = (event) => {
    setTargetType(event.target.value);
    // Reset role and plan when target type changes
    setTargetRole("");
    setTargetPlan("");
  };

  const historyColumns = [
    { Header: "notification", accessor: "notification", width: "50%", align: "left" },
    { Header: "target", accessor: "target", width: "25%", align: "left" },
    { Header: "sent at", accessor: "sentAt", align: "center" },
  ];

  useEffect(() => {
    fetchNotificationHistory();
  }, [historyPage, historyLimit]);

  // Fetch plans when target role or type changes
  useEffect(() => {
    const fetchPlansForRole = async () => {
      if (targetType === "plan" || targetType === "role_and_plan") {
        if (targetRole && targetType === "role_and_plan") {
          // When "By Role and Plan" is selected and role is chosen, filter plans by role
          if (targetRole === "admin") {
            // Admins don't have subscription plans
            setAvailablePlans([]);
          } else {
            try {
              const response = await getPlansByRole(targetRole);
              setAvailablePlans(response.data || []);
            } catch (error) {
              console.error("Error fetching plans for role:", error);
              setAvailablePlans([]);
            }
          }
        } else if (targetType === "plan") {
          // When "By Plan" is selected without role, show all plans
          const allPlans = [
            "Journey to Join",
            "Smart Search",
            "Starter Recruiter",
            "Catch & Keep",
            "Rapid Hire",
            "Recruit & Conquer"
          ];
          setAvailablePlans(allPlans);
        } else {
          // "By Role and Plan" selected but no role chosen yet
          setAvailablePlans([]);
        }
      } else {
        setAvailablePlans([]);
      }
    };

    fetchPlansForRole();
  }, [targetRole, targetType]);

  const fetchNotificationHistory = async () => {
    try {
      const response = await getNotificationHistory(historyPage, historyLimit);
      setNotificationHistory(response.data);
      setHistoryTotal(response.pagination?.total || 0);
      setTotalPages(Math.ceil((response.pagination?.total || 0) / historyLimit));
    } catch (error) {
      console.error("Error fetching notification history:", error);
    }
  };

  const handlePageChange = (event, value) => {
    setHistoryPage(value);
  };



  const handleSendNotification = async () => {
    if (!title.trim() || !message.trim()) return;

    setLoading(true);
    setSendSuccess(false);

    try {
      await sendNotification({
        title: title.trim(),
        body: message.trim(),
        targetType,
        targetRole: targetType === "role" || targetType === "role_and_plan" ? targetRole : null,
        targetPlan: targetType === "plan" || targetType === "role_and_plan" ? targetPlan : null,
      });

      // Reset form
      setTitle("");
      setMessage("");
      setTargetType("all");
      setTargetRole("");
      setTargetPlan("");
      setSendSuccess(true);

      // Refresh history and reset to first page
      setHistoryPage(1);
      fetchNotificationHistory();

      // Hide success message after 5 seconds
      setTimeout(() => setSendSuccess(false), 5000);
    } catch (error) {
      console.error("Error sending notification:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };



  const historyRows = notificationHistory.map((notification) => ({
    notification: (
      <MDBox sx={{
        width: '100%',
        maxWidth: { xs: '250px', sm: '350px', md: '400px' },
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        whiteSpace: 'normal'
      }}>
        <MDTypography
          variant="body2"
          fontWeight="bold"
          sx={{
            marginBottom: '4px',
            fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' },
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            lineHeight: 1.3
          }}
        >
          {notification.title}
        </MDTypography>
        <MDTypography
          variant="caption"
          color="text"
          sx={{
            fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.75rem' },
            lineHeight: 1.4,
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            whiteSpace: 'normal',
            display: 'block'
          }}
        >
          {notification.body}
        </MDTypography>
      </MDBox>
    ),
    target: (
      <MDBox sx={{ minWidth: { xs: '70px', sm: '100px' } }}>
        <MDTypography
          variant="caption"
          color="text"
          fontWeight="medium"
          sx={{
            fontSize: { xs: '0.65rem', sm: '0.75rem' },
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            lineHeight: 1.3
          }}
        >
          { notification.targetType || 'All Users' }
        </MDTypography>
      </MDBox>
    ),
    sentAt: (
      <MDBox sx={{ minWidth: { xs: '90px', sm: '120px' } }}>
        <MDTypography
          variant="caption"
          color="text"
          fontWeight="medium"
          sx={{
            fontSize: { xs: '0.65rem', sm: '0.75rem' },
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            lineHeight: 1.3
          }}
        >
          {formatDate(notification.createdAt)}
        </MDTypography>
      </MDBox>
    ),
  }));

  const renderSendNotificationTab = () => (
    <MDBox>
      <MDTypography variant="h6" mb={3}>
        Compose Notification
      </MDTypography>

      {sendSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Notification sent successfully!
        </Alert>
      )}

      <MDBox component="form" role="form">
        <MDBox mb={2}>
          <TextField
            fullWidth
            label="Notification Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            variant="outlined"
          />
        </MDBox>

        <MDBox mb={2}>
          <TextField
            fullWidth
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            variant="outlined"
            multiline
            rows={4}
          />
        </MDBox>

        <MDBox mb={2}>
          <FormControl fullWidth>
            <InputLabel>Target Audience</InputLabel>
            <Select
              value={targetType}
              label="Target Audience"
              onChange={handleTargetTypeChange}
              sx={{
                height: "56px",
                fontSize: "14px",
                "& .MuiSelect-select": {
                  padding: "16px 24px 16px 14px",
                },
              }}
            >
              <MenuItem value="all">All Users</MenuItem>
              <MenuItem value="role">By Role</MenuItem>
              <MenuItem value="plan">By Plan</MenuItem>
              <MenuItem value="role_and_plan">By Role and Plan</MenuItem>
            </Select>
          </FormControl>
        </MDBox>

        {(targetType === "role" || targetType === "role_and_plan") && (
          <MDBox mb={2}>
            <FormControl fullWidth>
              <InputLabel>User Role</InputLabel>
              <Select
                value={targetRole}
                label="User Role"
                onChange={handleRoleChange}
                sx={{
                  height: "56px",
                  fontSize: "14px",
                  "& .MuiSelect-select": {
                    padding: "16px 24px 16px 14px",
                  },
                }}
              >
                <MenuItem value="candidate">Candidates</MenuItem>
                <MenuItem value="recruiter">Recruiters</MenuItem>
                <MenuItem value="admin">Admins</MenuItem>
              </Select>
            </FormControl>
          </MDBox>
        )}

        {(targetType === "plan" || targetType === "role_and_plan") && targetRole !== "admin" && (
          <MDBox mb={2}>
            <FormControl fullWidth>
              <InputLabel>Subscription Plan</InputLabel>
              <Select
                value={targetPlan}
                label="Subscription Plan"
                onChange={(e) => setTargetPlan(e.target.value)}
                sx={{
                  height: "56px",
                  fontSize: "14px",
                  "& .MuiSelect-select": {
                    padding: "16px 24px 16px 14px",
                  },
                }}
              >
                {availablePlans.length === 0 ? (
                  <MenuItem value="None">None</MenuItem>
                ) : (
                  availablePlans.map((plan) => (
                    <MenuItem key={plan} value={plan}>
                      {plan}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </MDBox>
        )}

        <MDBox display="flex" justifyContent="flex-end" alignItems="center" mt={3} mb={2}>
          <MDButton
            variant="contained"
            color="primary"
            onClick={handleSendNotification}
            disabled={!title.trim() || !message.trim() || loading}
          >
            {loading ? "Sending..." : "Send Notification"}
          </MDButton>
        </MDBox>
      </MDBox>
    </MDBox>
  );

  const renderHistoryTab = () => (
    <MDBox>
      <MDBox sx={{
        '& table': {
          tableLayout: { xs: 'auto', md: 'fixed' },
          width: '100% !important'
        },
        '& .MuiTableCell-root': {
          whiteSpace: 'normal !important',
          wordBreak: 'break-word !important',
          overflowWrap: 'break-word !important',
          verticalAlign: 'top !important',
          padding: { xs: '8px 4px !important', sm: '12px 8px !important', md: '16px 12px !important' },
          overflow: 'visible !important',
          fontSize: { xs: '0.75rem', sm: '0.875rem' }
        },
        // Desktop column widths
        '& .MuiTableCell-root:first-of-type': {
          width: { xs: 'auto', md: '50% !important' },
          maxWidth: { xs: 'none', md: '400px !important' },
          minWidth: { xs: '200px', md: '300px !important' }
        },
        '& .MuiTableCell-root:nth-of-type(2)': {
          width: { xs: 'auto', md: '25% !important' },
          maxWidth: { xs: 'none', md: '150px !important' },
          minWidth: { xs: '80px', md: 'auto' }
        },
        '& .MuiTableCell-root:nth-of-type(3)': {
          width: { xs: 'auto', md: '25% !important' },
          maxWidth: { xs: 'none', md: '150px !important' },
          minWidth: { xs: '100px', md: 'auto' }
        },
        '& .MuiTableRow-root': {
          height: 'auto !important'
        },
        '& .MuiTypography-root': {
          whiteSpace: 'normal !important',
          wordBreak: 'break-word !important',
          overflowWrap: 'break-word !important',
          fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }
        },
        // Mobile specific styles
        '@media (max-width: 768px)': {
          '& .MuiTableContainer-root': {
            overflowX: 'auto'
          },
          '& table': {
            minWidth: '100%'
          }
        }
      }}>
        <DataTable
          table={{ columns: historyColumns, rows: historyRows }}
          isSorted={false}
          entriesPerPage={false}
          showTotalEntries={false}
          noEndBorder
        />
      </MDBox>
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="center"
        mb={2}
        mt={2}
        px={3}
      >
        <Pagination
          count={totalPages}
          page={historyPage}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
          sx={{ ml: 2 }}
        />
      </MDBox>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Notifications Management
                </MDTypography>
              </MDBox>
              <MDBox pt={3} px={3}>
                <Box
                  sx={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: "12px",
                    padding: "4px",
                    marginBottom: "24px",
                  }}
                >
                  <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    sx={{
                      "& .MuiTabs-indicator": {
                        height: "100%",
                        borderRadius: "8px",
                        backgroundColor: "white",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        transition: "all 300ms ease",
                      },
                      "& .MuiTab-root": {
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: "14px",
                        minHeight: "40px",
                        borderRadius: "8px",
                        margin: "0 2px",
                        color: "#6c757d",
                        "&.Mui-selected": {
                          color: "#495057",
                          fontWeight: 600,
                        },
                      },
                    }}
                  >
                    {tabs.map((tab, index) => (
                      <Tab key={index} label={tab.label} />
                    ))}
                  </Tabs>
                </Box>
              </MDBox>
              <MDBox px={3}>
                {selectedTab === 0 && renderSendNotificationTab()}
                {selectedTab === 1 && renderHistoryTab()}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Notifications;
